/**
 * Slideshow Section JavaScript
 * - Handles slide navigation (arrows/dots)
 * - Autoplay functionality
 * - Touch/swipe support for mobile
 * - Keyboard navigation
 */

(function() {
  'use strict';

  class Slideshow {
    constructor(section) {
      this.section = section;
      this.wrapper = section.querySelector('.slideshow__wrapper');
      this.slides = section.querySelectorAll('.slideshow__slide');
      this.dots = section.querySelectorAll('.slideshow__dot');
      this.prevBtn = section.querySelector('.slideshow__arrow--prev');
      this.nextBtn = section.querySelector('.slideshow__arrow--next');
      
      this.currentIndex = 0;
      this.totalSlides = this.slides.length;
      this.autoplayEnabled = section.dataset.autoplay === 'true';
      this.autoplayInterval = parseInt(section.dataset.interval, 10) || 5000;
      this.autoplayTimer = null;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.resizeRaf = null;
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.mediaElements = Array.from(
        section.querySelectorAll('.slideshow__image')
      );

      this.updateWrapperHeight = this.updateWrapperHeight.bind(this);

      this.init();
    }

    init() {
      this.bindEvents();
      this.bindMediaEvents();
      this.updateWrapperHeight();
      
      if (this.totalSlides > 1 && this.autoplayEnabled) {
        this.startAutoplay();
      }
    }

    bindEvents() {
      // Arrow navigation
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.prevSlide();
          this.resetAutoplay();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.nextSlide();
          this.resetAutoplay();
        });
      }

      // Dot navigation
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(index);
          this.resetAutoplay();
        });
      });

      // Keyboard navigation
      this.wrapper.addEventListener('keydown', (e) => {
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.prevSlide();
            this.resetAutoplay();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.nextSlide();
            this.resetAutoplay();
            break;
          case 'Home':
            e.preventDefault();
            this.goToSlide(0);
            this.resetAutoplay();
            break;
          case 'End':
            e.preventDefault();
            this.goToSlide(this.totalSlides - 1);
            this.resetAutoplay();
            break;
        }
      });

      // Touch/swipe support
      this.wrapper.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.wrapper.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      }, { passive: true });

      this.wrapper.addEventListener('mouseenter', () => {
        if (this.autoplayEnabled) {
          this.stopAutoplay();
        }
      });

      this.wrapper.addEventListener('mouseleave', () => {
        if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      });

      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('orientationchange', this.handleResize, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    bindMediaEvents() {
      this.mediaElements.forEach((media) => {
        if (media.complete) {
          return;
        }

        media.addEventListener('load', this.updateWrapperHeight);
        media.addEventListener('error', this.updateWrapperHeight);
      });
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        return;
      }

      if (this.autoplayEnabled && this.totalSlides > 1) {
        this.startAutoplay();
      }

      this.updateWrapperHeight();
    }

    handleResize() {
      if (this.resizeRaf) {
        window.cancelAnimationFrame(this.resizeRaf);
      }

      this.resizeRaf = window.requestAnimationFrame(() => {
        this.updateWrapperHeight();
        this.resizeRaf = null;
      });
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next slide
          this.nextSlide();
        } else {
          // Swipe right - previous slide
          this.prevSlide();
        }
        this.resetAutoplay();
      }
    }

    goToSlide(index) {
      if (index === this.currentIndex || index < 0 || index >= this.totalSlides) {
        return;
      }

      // Update slides
      this.slides[this.currentIndex].classList.remove('is-active');
      this.slides[index].classList.add('is-active');

      // Update dots
      if (this.dots.length) {
        this.dots[this.currentIndex].classList.remove('is-active');
        this.dots[this.currentIndex].setAttribute('aria-selected', 'false');
        this.dots[index].classList.add('is-active');
        this.dots[index].setAttribute('aria-selected', 'true');
      }

      // Announce slide change for screen readers
      this.announceSlideChange(index);

      this.currentIndex = index;
      this.updateWrapperHeight();
    }

    nextSlide() {
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;
      this.goToSlide(nextIndex);
    }

    prevSlide() {
      const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      this.goToSlide(prevIndex);
    }

    startAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
      }
      
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resetAutoplay() {
      if (this.autoplayEnabled) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    announceSlideChange(index) {
      // Create or update live region for screen reader announcements
      let liveRegion = this.section.querySelector('.slideshow__live-region');
      
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.className = 'slideshow__live-region visually-hidden';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        this.section.appendChild(liveRegion);
      }

      const slideNumber = index + 1;
      liveRegion.textContent = `Slide ${slideNumber} of ${this.totalSlides}`;
    }

    updateWrapperHeight() {
      if (!this.wrapper || !this.slides.length) {
        return;
      }

      const activeSlide = this.slides[this.currentIndex];

      if (!activeSlide) {
        return;
      }

      const desktopWrapper = activeSlide.querySelector('.slideshow__image-wrapper--desktop');
      const mobileWrapper = activeSlide.querySelector('.slideshow__image-wrapper--mobile');
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const activeMedia = isMobile && mobileWrapper ? mobileWrapper : desktopWrapper || mobileWrapper;

      if (!activeMedia) {
        this.wrapper.style.removeProperty('height');
        return;
      }

      const height = activeMedia.getBoundingClientRect().height;

      if (height > 0) {
        this.wrapper.style.height = `${Math.round(height)}px`;
      }
    }

    destroy() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('orientationchange', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.mediaElements.forEach((media) => {
        media.removeEventListener('load', this.updateWrapperHeight);
        media.removeEventListener('error', this.updateWrapperHeight);
      });

      if (this.resizeRaf) {
        window.cancelAnimationFrame(this.resizeRaf);
      }
    }
  }

  if (!customElements.get('slideshow-section')) {
  class SlideshowSection extends HTMLElement {
    connectedCallback() {
      if (!this.slideshowInstance) {
        this.slideshowInstance = new Slideshow(this);
      }
    }
    disconnectedCallback() {
      if (this.slideshowInstance && typeof this.slideshowInstance.destroy === 'function') {
        this.slideshowInstance.destroy();
      }
    }
  }
  customElements.define('slideshow-section', SlideshowSection);
}
})();
